import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { getAssignedDataGroup, setAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useDataGroupLoadQuery, useDataGroupUpdateMutation } from "../../services/server/DataGroupApi";
import type { DataGroupEntity } from "../../types/DataGroupEntity";

interface DataGroupFormProps extends FormProps {
    onAccessErrorChange?: (hasError: boolean) => void;
}

export const DataGroupEditForm: FC<DataGroupFormProps> = ({ runSubmit, formAction, onAccessErrorChange }) => {
    const { id } = useParams();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.DATA_GROUPS}`);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("data-group");

    const [formData, setFormData] = useState<DataGroupEntity>({} as DataGroupEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress, error: accessError } = useDataGroupLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            onAccessErrorChange?.(false);
        } else if (accessError) {
            onAccessErrorChange?.(true);
        }
    }, [loadFormData, accessError, onAccessErrorChange]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useDataGroupUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });
            const hasError = "error" in result;
            if (!hasError) {
                const assignedDataGroup = getAssignedDataGroup();
                // Keep selected data group in sync so UI updates immediately without re-login.
                if (assignedDataGroup?.id === result.data.id) {
                    setAssignedDataGroup(result.data);
                }

                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "DataGroup" }));
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || loadFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<DataGroupEntity>
                    name="DataGroupEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
